/**
 * ShelfMapConfigPanel – shelf map layout configuration and viewer.
 *
 * Issue: #214 – CORE – Warehouse Shelf Map – Frontend Configuration Flow Integration
 *
 * Accessible from the existing Configuration Management → Locations tab.
 * Allows users to:
 *   - View existing shelf map layouts for a warehouse
 *   - Create new layouts (bound to a root location entity)
 *   - Edit layout name/description/active state
 *   - Delete a layout
 *   - Open the interactive SVG map for a layout
 *   - Use the location details drawer when a slot is clicked
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Map as MapIcon,
  Close,
  Settings,
  Search,
  Check,
} from '@mui/icons-material';
import {
  useShelfMapLayouts,
  useShelfMapLayout,
  useShelfMapComposition,
  useShelfMapInventorySearch,
} from '@/hooks/queries/shelf-map/useShelfMapLayouts';
import {
  useCreateShelfMapLayout,
  useDeleteShelfMapLayout,
  useUpdateShelfMapLayout,
  useBatchCreateShelfMapSlots,
  useDeleteShelfMapSlot,
} from '@/hooks/mutations/shelf-map/useShelfMapMutations';
import { useNotifications } from '@/stores/useNotificationStore';
import { ShelfMapRenderer } from './ShelfMapRenderer';
import { ShelfMapDrawer } from './ShelfMapDrawer';
import { shelfMapService, type ShelfMapLayout } from '@/services/shelfMapService';
import { useModuleConfig } from '@/hooks/queries/admin/useModuleConfig';
import { useUpdateConfig } from '@/hooks/mutations/admin/useUpdateConfig';
import type { LocationsModuleConfig, ModuleConfig } from '@/types/moduleConfig';
import {
  useRootLocations,
  useLocationTemplates,
} from '@/hooks/queries/shelf-map/useShelfMapLayouts';
import type { RootLocation } from '@/hooks/queries/shelf-map/useShelfMapLayouts';

// ---------------------------------------------------------------------------
// Configure Slots dialog
// ---------------------------------------------------------------------------

interface ConfigureSlotsDialogProps {
  open: boolean;
  layout: ShelfMapLayout | null;
  onClose: () => void;
}

const ConfigureSlotsDialog: React.FC<ConfigureSlotsDialogProps> = ({ open, layout, onClose }) => {
  const layoutId = layout?.id ?? null;
  const { data: layoutWithSlots, isLoading: slotsLoading } = useShelfMapLayout(
    open ? layoutId : null,
  );
  const { mutateAsync: batchCreateSlots } = useBatchCreateShelfMapSlots();
  const { mutateAsync: deleteSlot } = useDeleteShelfMapSlot();
  const notifications = useNotifications();

  const [generatingFromLocations, setGeneratingFromLocations] = useState(false);
  const [deletingSlotId, setDeletingSlotId] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const { data: composition, isLoading: compositionLoading } = useShelfMapComposition(
    open ? layoutId : null,
  );
  const { data: templates = [] } = useLocationTemplates();

  // Pre-select the dropdown to the warehouse-bound template when composition loads
  useEffect(() => {
    if (composition?.template_id) {
      setSelectedTemplateId(composition.template_id);
    }
  }, [composition?.template_id]);

  const existingSlots = layoutWithSlots?.slots ?? [];

  const existingKey = (aisle: string, shelf: number, col: number) => `${aisle}-${shelf}-${col}`;
  const existingSet = new Set(
    existingSlots.map((s) => existingKey(s.aisle_label, s.shelf_number, s.column_number)),
  );

  // Count how many slots WILL be generated = leaf locations (no children within
  // the composition set). Intermediate nodes (aisles, shelves) are counted in
  // composition.locations but never produce a slot themselves.
  const expectedSlotCount = useMemo(() => {
    if (!composition?.locations.length) return 0;
    const locationIds = new Set(composition.locations.map((l) => l.id));
    const parentIds = new Set(
      composition.locations
        .filter((l) => l.parent_location_id && locationIds.has(l.parent_location_id))
        .map((l) => l.parent_location_id!),
    );
    return composition.locations.filter((l) => !parentIds.has(l.id)).length;
  }, [composition]);

  const handleGenerateFromLocations = async () => {
    if (!layoutId || !composition) return;
    const { locations } = composition;
    if (!locations.length) {
      notifications.warning(
        'No locations',
        'No locations found in the template composition for this layout.',
      );
      return;
    }

    // Build children map: parent_id → child locations
    const locationIds = new Set(locations.map((l) => l.id));
    const children = new Map<string, typeof locations>();
    for (const loc of locations) {
      const key =
        loc.parent_location_id && locationIds.has(loc.parent_location_id)
          ? loc.parent_location_id
          : '__root__';
      if (!children.has(key)) children.set(key, []);
      children.get(key)!.push(loc);
    }
    const sortLocs = (locs: typeof locations) =>
      [...locs].sort((a, b) =>
        (a.breadcrumb ?? a.display_name).localeCompare(b.breadcrumb ?? b.display_name),
      );

    // Top-level = locations whose parent is not within the composition set
    // (the warehouse root is excluded from the composition, so its direct
    // children always appear here)
    const topLevel = sortLocs(children.get('__root__') ?? []);

    const slotsToCreate: Array<{
      aisle_label: string;
      shelf_number: number;
      column_number: number;
      location_entity_id: string;
    }> = [];

    const aisleLabel = (loc: (typeof locations)[0], idx: number): string => {
      // Full sanitized display name so "Row A" and "Row B" produce distinct
      // labels ("ROWA" vs "ROWB") rather than colliding on the first character.
      const name = loc.display_name.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
      return name || String.fromCharCode(65 + (idx % 26));
    };

    // Recursively collect all leaf locations (no children) under a given node.
    // Works for arbitrary hierarchy depth — the node itself is returned when it
    // has no children, otherwise we descend into each child.
    const collectLeaves = (nodeId: string): typeof locations => {
      const kids = sortLocs(children.get(nodeId) ?? []);
      if (kids.length === 0) {
        const self = locations.find((l) => l.id === nodeId);
        return self ? [self] : [];
      }
      return kids.flatMap((k) => collectLeaves(k.id));
    };

    // Slot assignment strategy (works for any depth ≥ 0):
    //
    //   top-level node (aisle)   → aisle_label
    //   2nd-level node (shelf)   → shelf_number (running index within the aisle)
    //   leaf under shelf         → column_number (position within that shelf)
    //
    // For 1-level flat templates (no children at all):
    //   each top-level IS the leaf → aisle A, shelf 1, col 1..N
    //
    // For 2-level templates (aisle → bin):
    //   bins are direct children of aisles, no 2nd-level grouping:
    //   → aisle per top-level, shelf 1, cols 1..N
    //
    // For 3-level templates (aisle → shelf → bin):
    //   standard 3-level: aisle, shelf rows, bin columns
    //
    // For 4+ level templates (aisle → row → shelf → bin, etc.):
    //   mid-level nodes become shelf rows, ALL leaves under each mid-level
    //   node (regardless of how many intervening levels exist) become columns.
    //   This guarantees every leaf location receives exactly one slot.
    topLevel.forEach((aisle, ai) => {
      const label = aisleLabel(aisle, ai);
      const midLevel = sortLocs(children.get(aisle.id) ?? []);

      if (midLevel.length === 0) {
        // Aisle is itself a leaf — single slot for this location.
        slotsToCreate.push({
          aisle_label: label,
          shelf_number: 1,
          column_number: 1,
          location_entity_id: aisle.id,
        });
      } else {
        // Check whether any mid-level node has descendants (signals 3+ levels).
        const midHasChildren = midLevel.some((m) => (children.get(m.id) ?? []).length > 0);

        if (!midHasChildren) {
          // 2-level: mid-level nodes ARE the leaf positions — lay out as columns.
          midLevel.forEach((col, ci) => {
            slotsToCreate.push({
              aisle_label: label,
              shelf_number: 1,
              column_number: ci + 1,
              location_entity_id: col.id,
            });
          });
        } else {
          // 3+ levels: each mid-level node becomes a shelf row.
          // Recursively collect ALL leaves under it (handles 4, 5, … levels)
          // so no location is missed regardless of hierarchy depth.
          midLevel.forEach((shelf, si) => {
            const leaves = collectLeaves(shelf.id);
            if (leaves.length === 0) {
              // Mid-level node is itself a leaf (mixed-depth tree edge case).
              slotsToCreate.push({
                aisle_label: label,
                shelf_number: si + 1,
                column_number: 1,
                location_entity_id: shelf.id,
              });
            } else {
              leaves.forEach((leaf, ci) => {
                slotsToCreate.push({
                  aisle_label: label,
                  shelf_number: si + 1,
                  column_number: ci + 1,
                  location_entity_id: leaf.id,
                });
              });
            }
          });
        }
      }
    });

    // Pre-filter client-side to skip slots that are already in the existing set;
    // the server also deduplicates via ON CONFLICT DO NOTHING, but filtering here
    // reduces payload size and gives an accurate client-side skipped count.
    const clientSkipped = slotsToCreate.filter((s) =>
      existingSet.has(existingKey(s.aisle_label, s.shelf_number, s.column_number)),
    ).length;
    const newSlots = slotsToCreate.filter(
      (s) => !existingSet.has(existingKey(s.aisle_label, s.shelf_number, s.column_number)),
    );

    if (!newSlots.length) {
      notifications.warning(
        'Nothing to create',
        clientSkipped > 0
          ? `All ${clientSkipped} slots already exist.`
          : 'Could not derive any slots from the location hierarchy.',
      );
      return;
    }

    // Send in chunks of 500 to respect the API batch limit
    const BATCH_SIZE = 500;
    const chunks: (typeof newSlots)[] = [];
    for (let i = 0; i < newSlots.length; i += BATCH_SIZE) {
      chunks.push(newSlots.slice(i, i + BATCH_SIZE));
    }

    setGeneratingFromLocations(true);
    let totalCreated = 0;
    let totalSkipped = clientSkipped;
    try {
      for (const chunk of chunks) {
        const result = await batchCreateSlots({ layoutId, slots: chunk });
        totalCreated += result.created;
        totalSkipped += result.skipped;
      }
      notifications.success(
        `Generated ${totalCreated} slot${totalCreated !== 1 ? 's' : ''} from location data${
          totalSkipped ? ` (${totalSkipped} skipped)` : ''
        }`,
      );
    } catch {
      notifications.error('Failed to generate slots from location data');
    } finally {
      setGeneratingFromLocations(false);
    }
  };

  const handleDeleteSlot = async (slotId: string) => {
    if (!layoutId) return;
    setDeletingSlotId(slotId);
    try {
      await deleteSlot({ layoutId, slotId });
    } catch {
      notifications.error('Failed to delete slot');
    } finally {
      setDeletingSlotId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Configure Slots — {layout?.name ?? ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Generate from template */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Generate from Template
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Select a template to automatically create slots from its section location hierarchy.
              Each slot is linked to its real location entity. Already-existing slots are skipped.
            </Typography>
            {compositionLoading && <Skeleton variant="rounded" height={36} sx={{ mb: 1 }} />}
            {!compositionLoading && (
              <Stack spacing={1.5}>
                <TextField
                  select
                  label="Template"
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  size="small"
                  fullWidth
                  SelectProps={{ native: true }}
                  disabled={generatingFromLocations}
                >
                  <option value="">— Select a template —</option>
                  {templates
                    .filter((t) => /cif|kiosk/i.test(t.display_name))
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.display_name}
                      </option>
                    ))}
                </TextField>
                {selectedTemplateId && composition?.template_id !== selectedTemplateId && (
                  <Alert severity="info">
                    This section is bound to a different template. Update the section template
                    binding to generate from the selected template.
                  </Alert>
                )}
                {selectedTemplateId &&
                  composition?.template_id === selectedTemplateId &&
                  composition.locations.length === 0 && (
                    <Alert severity="warning">
                      No locations found for this template in the section hierarchy. Assign
                      locations to the template first.
                    </Alert>
                  )}
                {selectedTemplateId &&
                  composition?.template_id === selectedTemplateId &&
                  composition.locations.length > 0 && (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        {expectedSlotCount} slot{expectedSlotCount !== 1 ? 's' : ''} to generate
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.disabled"
                          sx={{ ml: 0.5 }}
                        >
                          ({composition.locations.length} locations in hierarchy)
                        </Typography>
                      </Typography>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => void handleGenerateFromLocations()}
                        disabled={generatingFromLocations}
                        startIcon={
                          generatingFromLocations ? <CircularProgress size={16} /> : <Add />
                        }
                      >
                        Generate from Locations
                      </Button>
                    </Stack>
                  )}
                {generatingFromLocations && (
                  <LinearProgress sx={{ mt: 1.5, borderRadius: 1 }} color="success" />
                )}
              </Stack>
            )}
          </Box>

          <Divider />

          {/* Existing slots */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">
                Existing Slots
                {existingSlots.length > 0 && (
                  <Chip
                    label={existingSlots.length}
                    size="small"
                    sx={{ ml: 1 }}
                    variant="outlined"
                  />
                )}
              </Typography>
            </Stack>

            {slotsLoading && <Skeleton variant="rounded" height={120} />}

            {!slotsLoading && existingSlots.length === 0 && (
              <Alert severity="info">
                No slots yet. Use the template generator above to create slots.
              </Alert>
            )}

            {!slotsLoading && existingSlots.length > 0 && (
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Row</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Shelf</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {existingSlots.map((slot) => (
                      <TableRow key={slot.id} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                          {slot.aisle_label}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                          {slot.shelf_number}
                        </TableCell>
                        <TableCell>
                          {/* Show the most specific location name (Issue #1081); the full
                              breadcrumb is only shown via tooltip when it adds path context
                              beyond the specific name (avoids duplicate tooltips like
                              "storefront, storefront" for top-level/root locations). */}
                          <Tooltip
                            title={
                              slot.location_breadcrumb &&
                              slot.location_breadcrumb !== slot.location_name
                                ? slot.location_breadcrumb
                                : ''
                            }
                            disableHoverListener={
                              !slot.location_breadcrumb ||
                              slot.location_breadcrumb === slot.location_name
                            }
                          >
                            <Typography
                              variant="body2"
                              color={
                                (slot.location_name ?? slot.location_breadcrumb)
                                  ? 'text.primary'
                                  : 'text.disabled'
                              }
                            >
                              {slot.location_name ??
                                slot.location_breadcrumb ??
                                slot.label_override ??
                                '—'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => void handleDeleteSlot(slot.id)}
                            disabled={deletingSlotId === slot.id}
                            aria-label={`Delete slot row ${slot.aisle_label} shelf ${slot.shelf_number}`}
                          >
                            {deletingSlotId === slot.id ? (
                              <CircularProgress size={14} />
                            ) : (
                              <Delete fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Create / Edit layout dialog
// ---------------------------------------------------------------------------

interface LayoutDialogProps {
  open: boolean;
  existing?: ShelfMapLayout | null;
  warehouses: RootLocation[];
  templateMap: Map<string, string>;
  onClose: () => void;
  onSaved: () => void;
}

const LayoutDialog: React.FC<LayoutDialogProps> = ({
  open,
  existing,
  warehouses,
  templateMap,
  onClose,
  onSaved,
}) => {
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [warehouseId, setWarehouseId] = useState(existing?.warehouse_entity_id ?? '');
  const [isActive, setIsActive] = useState(existing?.is_active ?? true);
  const [saving, setSaving] = useState(false);

  const { mutateAsync: create } = useCreateShelfMapLayout();
  const { mutateAsync: update } = useUpdateShelfMapLayout();
  const notifications = useNotifications();

  const isEdit = !!existing;
  const isValid = name.trim().length > 0 && (isEdit || warehouseId.length > 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await update({
          layoutId: existing!.id,
          input: {
            name: name.trim(),
            description: description.trim() || null,
            is_active: isActive,
          },
        });
        notifications.success('Layout updated');
      } else {
        await create({
          name: name.trim(),
          description: description.trim() || null,
          warehouse_entity_id: warehouseId,
          is_active: isActive,
        });
        notifications.success('Layout created');
      }
      onSaved();
      onClose();
    } catch {
      notifications.error(isEdit ? 'Failed to update layout' : 'Failed to create layout');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Layout' : 'Create Layout'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Layout Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            size="small"
            InputLabelProps={{ shrink: name.length > 0 }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
            InputLabelProps={{ shrink: description.length > 0 }}
          />
          {!isEdit && (
            <TextField
              select
              label="Section"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              required
              fullWidth
              size="small"
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="">— Select a section —</option>
              {warehouses.map((wh) => {
                const templateName = wh.entity_data?.template_id
                  ? templateMap.get(wh.entity_data.template_id)
                  : undefined;
                return (
                  <option key={wh.id} value={wh.id}>
                    {wh.display_name}
                    {templateName ? ` (${templateName})` : ''}
                  </option>
                );
              })}
            </TextField>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                size="small"
              />
            }
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={!isValid || saving}>
          {saving ? <CircularProgress size={16} /> : isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Map viewer – full-screen-ish overlay for a selected layout
// ---------------------------------------------------------------------------

interface MapViewerProps {
  layoutId: string;
  onClose: () => void;
}

const MapViewer: React.FC<MapViewerProps> = ({ layoutId, onClose }) => {
  const { data: layout, isLoading, isError } = useShelfMapLayout(layoutId);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 350);

  // This is a read-only browse/expand view (Configuration Management → Locations
  // → shelf map layouts). Slot drag/move is intentionally NOT wired here — see
  // issue #1082. Dragging is only ever enabled when a caller passes `onMoveSlot`
  // to ShelfMapRenderer, so simply not passing it keeps browsing/searching/zoom
  // fully interactive while preventing any destructive cell moves.

  const handleSelectSlot = (slotId: string, locationEntityId: string | null) => {
    setSelectedSlotId(slotId);
    setSelectedLocationId(locationEntityId);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedSlotId(null);
    setSelectedLocationId(null);
  };

  const selectedSlot = layout?.slots.find((s) => s.id === selectedSlotId);
  const selectedSlotLabel = selectedSlot?.location_name ?? null;
  const selectedSlotBreadcrumb = selectedSlot?.location_breadcrumb ?? null;
  const selectedSlotAisle = selectedSlot?.aisle_label ?? null;

  const mappedLocationIds = useMemo(() => {
    if (!layout) return [];
    return Array.from(
      new Set(layout.slots.map((slot) => slot.location_entity_id).filter(Boolean) as string[]),
    );
  }, [layout]);

  const inventoryPresenceQuery = useQuery({
    queryKey: ['shelf-map', 'inventory-presence-bulk', layoutId, mappedLocationIds],
    queryFn: () => shelfMapService.getLocationInventoryPresenceBulk(layoutId, mappedLocationIds),
    enabled: !!layout && mappedLocationIds.length > 0,
    staleTime: 60_000,
    retry: 1,
    gcTime: 0,
  });

  const locationIdsWithInventory = useMemo(() => {
    const ids = new Set<string>();
    if (inventoryPresenceQuery.status !== 'success') return ids;

    for (const location of inventoryPresenceQuery.data.locations) {
      if (location.has_inventory) ids.add(location.location_id);
    }

    return ids;
  }, [inventoryPresenceQuery]);

  const resolvedLocationIds = useMemo(() => {
    if (inventoryPresenceQuery.status !== 'success') return new Set<string>();
    return new Set(mappedLocationIds);
  }, [inventoryPresenceQuery.status, mappedLocationIds]);

  const highlightedSlotIds = useMemo(() => {
    if (!layout) return new Set<string>();
    return new Set(
      layout.slots
        .filter(
          (slot) =>
            slot.location_entity_id != null &&
            locationIdsWithInventory.has(slot.location_entity_id),
        )
        .map((slot) => slot.id),
    );
  }, [layout, locationIdsWithInventory]);

  const noInventorySlotIds = useMemo(() => {
    if (!layout) return new Set<string>();
    return new Set(
      layout.slots
        .filter(
          (slot) =>
            slot.location_entity_id != null &&
            resolvedLocationIds.has(slot.location_entity_id) &&
            !locationIdsWithInventory.has(slot.location_entity_id),
        )
        .map((slot) => slot.id),
    );
  }, [layout, locationIdsWithInventory, resolvedLocationIds]);

  const inventoryPendingCount = inventoryPresenceQuery.isPending ? mappedLocationIds.length : 0;
  const inventoryErrorCount = inventoryPresenceQuery.isError ? mappedLocationIds.length : 0;

  // ── Inventory search (LIN / nomenclature) ─────────────────────────────
  const searchQuery = useShelfMapInventorySearch(layoutId, debouncedSearch);
  const searchMatchLocationIds = useMemo(() => {
    if (!searchQuery.data?.matches.length) return new Set<string>();
    return new Set(searchQuery.data.matches.map((m) => m.location_entity_id));
  }, [searchQuery.data]);

  const searchHighlightedSlotIds = useMemo(() => {
    if (!layout || searchMatchLocationIds.size === 0) return new Set<string>();
    return new Set(
      layout.slots
        .filter(
          (slot) =>
            slot.location_entity_id != null && searchMatchLocationIds.has(slot.location_entity_id),
        )
        .map((slot) => slot.id),
    );
  }, [layout, searchMatchLocationIds]);

  return (
    <Dialog open fullScreen>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <MapIcon color="primary" fontSize="small" />
            <Typography variant="h6">{layout?.name ?? 'Shelf Map'}</Typography>
            {layout?.warehouse_name && (
              <Chip label={layout.warehouse_name} size="small" variant="outlined" />
            )}
          </Stack>
          <IconButton onClick={onClose} aria-label="Close map viewer">
            <Close />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip label={`Occupied: ${highlightedSlotIds.size}`} size="small" color="default" />
              <Chip label={`Empty: ${noInventorySlotIds.size}`} size="small" color="error" />
              {inventoryPendingCount > 0 && (
                <Chip
                  label={`Checking: ${inventoryPendingCount}`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
              {inventoryErrorCount > 0 && (
                <Chip
                  label={`Failed checks: ${inventoryErrorCount}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
              {debouncedSearch.trim().length >= 2 && (
                <Chip
                  label={
                    searchQuery.isFetching
                      ? 'Searching…'
                      : `Matches: ${searchHighlightedSlotIds.size}`
                  }
                  size="small"
                  color="success"
                  variant={searchHighlightedSlotIds.size > 0 ? 'filled' : 'outlined'}
                />
              )}
              <Box sx={{ flex: 1, minWidth: 180, maxWidth: 280, ml: 'auto' }}>
                <TextField
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  size="small"
                  placeholder="Search by LIN or nomenclature…"
                  inputProps={{ 'aria-label': 'Search by LIN or nomenclature' }}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiInputBase-root': { fontSize: 12 } }}
                />
              </Box>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            <Box sx={{ flex: 1, p: 2, overflow: 'hidden' }}>
              {isLoading && <Skeleton variant="rounded" height="100%" />}
              {isError && <Alert severity="error">Failed to load map layout.</Alert>}
              {layout && !isLoading && (
                <ShelfMapRenderer
                  slots={layout.slots}
                  selectedSlotId={selectedSlotId}
                  highlightedSlotIds={searchHighlightedSlotIds}
                  noInventorySlotIds={noInventorySlotIds}
                  onSelectSlot={handleSelectSlot}
                />
              )}
            </Box>

            {/* Details drawer — always mounted as flex sibling so it sits next
               to the map (not in a portal overlay). open/display controlled
               by the Box inside ShelfMapDrawer. */}
            <ShelfMapDrawer
              layoutId={layoutId}
              locationEntityId={selectedLocationId}
              slotLabel={selectedSlotLabel}
              slotBreadcrumb={selectedSlotBreadcrumb}
              aisleLabel={selectedSlotAisle}
              open={drawerOpen}
              onClose={handleCloseDrawer}
            />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export const ShelfMapConfigPanel: React.FC = () => {
  const { data: layouts = [], isLoading, isError, refetch } = useShelfMapLayouts();
  const { mutateAsync: deleteLayout } = useDeleteShelfMapLayout();
  const notifications = useNotifications();

  const { config: locationsConfig } = useModuleConfig<LocationsModuleConfig>('locations');
  const shelfMapLabels = locationsConfig.features.section_shelf_maps;
  const { mutateAsync: updateLocationsConfig } = useUpdateConfig();
  const [editingHeader, setEditingHeader] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [headerDraft, setHeaderDraft] = useState('');
  const [descDraft, setDescDraft] = useState('');

  const saveShelfMapLabels = async (next: { header: string; description: string }) => {
    const updated: ModuleConfig = {
      display_name: locationsConfig.display_name,
      features: { ...locationsConfig.features, section_shelf_maps: next },
    };
    try {
      await updateLocationsConfig({ configKey: 'module.locations', configValue: updated });
      notifications.success('Location map labels saved');
    } catch {
      notifications.error('Failed to save location map labels');
    }
  };

  const saveHeader = () => {
    setEditingHeader(false);
    void saveShelfMapLabels({
      header: headerDraft.trim() || shelfMapLabels.header,
      description: shelfMapLabels.description,
    });
  };

  const saveDesc = () => {
    setEditingDesc(false);
    void saveShelfMapLabels({
      header: shelfMapLabels.header,
      description: descDraft.trim() || shelfMapLabels.description,
    });
  };

  // Fetch only root locations (structural_type='root') via location_hierarchy
  // view. These represent warehouse/site boundaries — the valid targets for
  // shelf_map_layout.warehouse_entity_id.
  const { data: warehouses = [] } = useRootLocations();
  const { data: templates = [] } = useLocationTemplates();
  const templateMap = useMemo(
    () => new Map(templates.map((t) => [t.id, t.display_name])),
    [templates],
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ShelfMapLayout | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ShelfMapLayout | null>(null);
  const [configureTarget, setConfigureTarget] = useState<ShelfMapLayout | null>(null);
  const [viewLayoutId, setViewLayoutId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteLayout(deleteTarget.id);
      notifications.success(`Layout "${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      void refetch();
    } catch {
      notifications.error('Failed to delete layout');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box sx={{ flex: 1, pr: 2 }}>
          {editingHeader ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                value={headerDraft}
                onChange={(e) => setHeaderDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveHeader();
                  if (e.key === 'Escape') setEditingHeader(false);
                }}
                autoFocus
                sx={{ minWidth: 320 }}
              />
              <Tooltip title="Save">
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="Save header"
                  onClick={saveHeader}
                >
                  <Check fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton
                  size="small"
                  aria-label="Cancel editing header"
                  onClick={() => setEditingHeader(false)}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="h5">{shelfMapLabels.header}</Typography>
              <Tooltip title="Edit header">
                <IconButton
                  size="small"
                  aria-label="Edit header"
                  onClick={() => {
                    setHeaderDraft(shelfMapLabels.header);
                    setEditingHeader(true);
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}

          {editingDesc ? (
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 0.5 }}>
              <TextField
                size="small"
                value={descDraft}
                onChange={(e) => setDescDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setEditingDesc(false);
                }}
                autoFocus
                fullWidth
                multiline
              />
              <Tooltip title="Save">
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="Save description"
                  onClick={saveDesc}
                >
                  <Check fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton
                  size="small"
                  aria-label="Cancel editing description"
                  onClick={() => setEditingDesc(false)}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {shelfMapLabels.description}
              </Typography>
              <Tooltip title="Edit description">
                <IconButton
                  size="small"
                  sx={{ mt: -0.5 }}
                  aria-label="Edit description"
                  onClick={() => {
                    setDescDraft(shelfMapLabels.description);
                    setEditingDesc(true);
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          Create Layout
        </Button>
      </Stack>

      {isLoading && <Skeleton variant="rounded" height={200} />}

      {isError && !isLoading && <Alert severity="error">Failed to load shelf map layouts.</Alert>}

      {!isLoading && !isError && layouts.length === 0 && (
        <Alert severity="info">
          No map layouts defined. Create a layout to begin configuring your location maps.
        </Alert>
      )}

      {!isLoading && !isError && layouts.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Last Updated</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {layouts.map((layout) => (
                <TableRow key={layout.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {layout.name}
                    </Typography>
                    {layout.description && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {layout.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {layout.warehouse_name ?? layout.warehouse_identifier ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={layout.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={layout.is_active ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(layout.updated_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Configure Slots">
                        <IconButton
                          size="small"
                          onClick={() => setConfigureTarget(layout)}
                          aria-label={`Configure slots for ${layout.name}`}
                        >
                          <Settings fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Map">
                        <IconButton
                          size="small"
                          onClick={() => setViewLayoutId(layout.id)}
                          aria-label={`View map for ${layout.name}`}
                        >
                          <MapIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => setEditTarget(layout)}
                          aria-label={`Edit ${layout.name}`}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteTarget(layout)}
                          aria-label={`Delete ${layout.name}`}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Configure slots dialog */}
      <ConfigureSlotsDialog
        key={configureTarget?.id ?? 'configure-dialog'}
        open={!!configureTarget}
        layout={configureTarget}
        onClose={() => setConfigureTarget(null)}
      />

      {/* Create dialog – key remounts on each open so fields reset to empty */}
      <LayoutDialog
        key={createOpen ? 'create-dialog-open' : 'create-dialog-closed'}
        open={createOpen}
        warehouses={warehouses}
        templateMap={templateMap}
        onClose={() => setCreateOpen(false)}
        onSaved={() => void refetch()}
      />

      {/* Edit dialog – key forces remount per layout so useState re-initialises from existing */}
      <LayoutDialog
        key={editTarget?.id ?? 'edit-dialog'}
        open={!!editTarget}
        existing={editTarget}
        warehouses={warehouses}
        templateMap={templateMap}
        onClose={() => setEditTarget(null)}
        onSaved={() => void refetch()}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Layout</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete layout <strong>{deleteTarget?.name}</strong>? All slots
            within this layout will also be removed. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map viewer (full-screen) */}
      {viewLayoutId && <MapViewer layoutId={viewLayoutId} onClose={() => setViewLayoutId(null)} />}
    </Box>
  );
};
